import React, { useState, useEffect, useCallback } from 'react';
import { usersApi, orgSettingsApi } from '../../services/api';
import { useAuth } from '../../store/authContext';
import { PlusIcon, EyeIcon, EyeOffIcon } from '../../utils/icons';
import { formatDate } from '../../utils/formatters';
import Modal from '../../components/Modal';
import './UserList.css';

export default function UserList() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deactivatingUser, setDeactivatingUser] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError('');
      const [usersRes, settingsRes] = await Promise.all([
        usersApi.list(),
        orgSettingsApi.get(),
      ]);
      setUsers(usersRes.data);
      setSettings(settingsRes.data);
    } catch {
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeCount = users.filter((u) => u.is_active).length;
  const maxUsers = settings?.max_users || 0;
  const atLimit = maxUsers > 0 && activeCount >= maxUsers;

  const handleToggleAutoAssign = async () => {
    if (!settings) return;
    try {
      const res = await orgSettingsApi.update({ auto_assign_leads: !settings.auto_assign_leads });
      setSettings(res.data);
    } catch {
      setError('Failed to update setting.');
    }
  };

  const handleCreated = () => { setShowCreate(false); fetchData(); };
  const handleUpdated = () => { setViewingUser(null); fetchData(); };
  const handleDeleted = () => { setDeletingUser(null); fetchData(); };
  const handleDeactivated = () => { setDeactivatingUser(null); fetchData(); };

  if (loading) {
    return (
      <div className="um-page">
        <div className="um-loading">
          <div className="um-loading-spinner" />
          <p>Loading team...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="um-page">
      {/* Header */}
      <div className="page-header animate-fade-up">
        <div className="page-header-top">
          <div>
            <p className="section-label" style={{ marginBottom: 4 }}>Team Management</p>
            <h1 className="page-title">Team</h1>
            <p className="page-subtitle">{users.length} member{users.length !== 1 ? 's' : ''} in your organisation</p>
          </div>
          <div className="page-actions">
            <button
              className="btn btn-primary"
              onClick={() => setShowCreate(true)}
              disabled={atLimit}
              title={atLimit ? `User limit reached (${maxUsers})` : 'Add a new team member'}
            >
              <PlusIcon /> Add User
            </button>
          </div>
        </div>
      </div>

      {error && <div className="um-error animate-fade-up">{error}</div>}

      {/* User limit bar */}
      {settings && (
        <div className="um-limit-card card-sm animate-fade-up" style={{ animationDelay: '40ms' }}>
          <div className="um-limit-header">
            <span className="um-limit-text">
              <strong>{activeCount}</strong> of <strong>{maxUsers}</strong> user slots used
            </span>
            {atLimit && <span className="um-limit-warning">Limit reached</span>}
          </div>
          <div className="um-limit-track">
            <div
              className={`um-limit-fill ${atLimit ? 'um-limit-fill--full' : ''}`}
              style={{ width: `${Math.min((activeCount / maxUsers) * 100, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Auto-assign toggle */}
      {settings && (
        <div className="um-toggle-card card-sm animate-fade-up" style={{ animationDelay: '60ms' }}>
          <div className="um-toggle-row">
            <div className="um-toggle-info">
              <span className="um-toggle-label">Automatic lead assignment</span>
              <span className="um-toggle-desc">
                {settings.auto_assign_leads
                  ? 'New leads are automatically distributed across active team members'
                  : 'New leads are assigned to you (admin)'}
              </span>
            </div>
            <button
              className={`toggle-switch ${settings.auto_assign_leads ? 'toggle-switch--on' : ''}`}
              onClick={handleToggleAutoAssign}
              type="button"
            >
              <span className="toggle-thumb" />
            </button>
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="animate-fade-up" style={{ animationDelay: '80ms' }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
          <table className="um-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Modules</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="um-empty">
                    No team members yet. Add the first user to get started.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr
                    key={u.id}
                    className="um-row"
                    onClick={() => setViewingUser(u)}
                  >
                    <td>
                      <div className="um-name-cell">
                        <div className="um-avatar">
                          {u.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                        </div>
                        <span className="um-name">{u.name}</span>
                      </div>
                    </td>
                    <td className="um-email">{u.email}</td>
                    <td>
                      <span className={`um-role-badge um-role-badge--${u.role}`}>
                        {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                      </span>
                    </td>
                    <td>
                      {u.assigned_modules?.length > 0 ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {u.assigned_modules.map((m) => (
                            <span key={m} className={`module-badge module-badge--${m}`}>
                              {m.charAt(0).toUpperCase() + m.slice(1)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${u.is_active ? 'closed' : 'lost'}`}>
                        <span className="badge-dot" />
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="um-date">{formatDate(u.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <UserFormModal
          title="Add User"
          settings={settings}
          onClose={() => setShowCreate(false)}
          onSaved={handleCreated}
        />
      )}

      {/* View / Edit modal */}
      {viewingUser && (
        <UserDetailModal
          user={viewingUser}
          settings={settings}
          currentUserId={currentUser?.id}
          onClose={() => setViewingUser(null)}
          onSaved={handleUpdated}
          onDelete={(u) => { setViewingUser(null); setDeletingUser(u); }}
          onDeactivate={(u) => { setViewingUser(null); setDeactivatingUser(u); }}
        />
      )}

      {/* Delete confirmation */}
      {deletingUser && (
        <DeleteUserModal
          user={deletingUser}
          onClose={() => setDeletingUser(null)}
          onDeleted={handleDeleted}
        />
      )}

      {/* Deactivate confirmation */}
      {deactivatingUser && (
        <DeactivateUserModal
          user={deactivatingUser}
          onClose={() => setDeactivatingUser(null)}
          onDone={handleDeactivated}
        />
      )}
    </div>
  );
}


/* ─── User Form Modal (Create) ──────────────────────────────────────────── */

function UserFormModal({ title, settings, onClose, onSaved }) {
  const enabledModules = [];
  if (settings?.leads_module_enabled) enabledModules.push('leads');
  if (settings?.sales_module_enabled) enabledModules.push('sales');
  const showModuleSelect = enabledModules.length > 1;

  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'agent', assigned_modules: enabledModules.length === 1 ? [...enabledModules] : ['leads'] });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleModule = (mod) => {
    setForm((f) => {
      const current = f.assigned_modules;
      const has = current.includes(mod);
      if (has && current.length === 1) return f; // must keep at least one
      return { ...f, assigned_modules: has ? current.filter((m) => m !== mod) : [...current, mod] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      await usersApi.create(payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={title} size="sm" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          <div className="form-group">
            <label className="section-label">Name *</label>
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Full name"
              required
            />
          </div>

          <div className="form-group">
            <label className="section-label">Email *</label>
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="user@org.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="section-label">Password *</label>
            <div className="um-password-wrap">
              <input
                className="input"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Min 6 characters"
                required
                minLength={6}
              />
              <button
                type="button"
                className="um-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="section-label">Role *</label>
            <select
              className="input um-select"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            >
              <option value="admin">Admin</option>
              <option value="agent">Agent</option>
            </select>
          </div>

          {showModuleSelect && (
            <div className="form-group">
              <label className="section-label">Module Access *</label>
              <div className="um-checkbox-group">
                {enabledModules.map((mod) => (
                  <label key={mod} className="um-checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.assigned_modules.includes(mod)}
                      onChange={() => toggleModule(mod)}
                    />
                    <span>{mod.charAt(0).toUpperCase() + mod.slice(1)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creating...' : 'Create User'}
          </button>
        </div>
      </form>
    </Modal>
  );
}


/* ─── User Detail / Edit Modal ──────────────────────────────────────────── */

function UserDetailModal({ user, settings, currentUserId, onClose, onSaved, onDelete, onDeactivate }) {
  const enabledModules = [];
  if (settings?.leads_module_enabled) enabledModules.push('leads');
  if (settings?.sales_module_enabled) enabledModules.push('sales');
  const showModuleSelect = enabledModules.length > 1;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    password: '',
    role: user.role,
    assigned_modules: user.assigned_modules || ['leads'],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState('');

  const isSelf = String(user.id) === currentUserId;

  const toggleModule = (mod) => {
    setForm((f) => {
      const current = f.assigned_modules;
      const has = current.includes(mod);
      if (has && current.length === 1) return f;
      return { ...f, assigned_modules: has ? current.filter((m) => m !== mod) : [...current, mod] };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const updates = {};
      if (form.name !== user.name) updates.name = form.name;
      if (form.email !== user.email) updates.email = form.email;
      if (form.role !== user.role) updates.role = form.role;
      if (form.password) updates.password = form.password;
      const modsChanged = JSON.stringify([...form.assigned_modules].sort()) !== JSON.stringify([...(user.assigned_modules || [])].sort());
      if (modsChanged) {
        updates.assigned_modules = form.assigned_modules;
      }
      if (Object.keys(updates).length > 0) {
        await usersApi.update(user.id, updates);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    setError('');
    try {
      await usersApi.update(user.id, { is_active: true });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to activate. User limit may have been reached.');
    } finally {
      setActivating(false);
    }
  };

  return (
    <Modal title={editing ? 'Edit User' : 'User Details'} onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}

        {!editing ? (
          /* ── View mode ── */
          <div className="um-detail-grid">
            <div className="um-detail-row">
              <span className="um-detail-label">Name</span>
              <span className="um-detail-value">{user.name}</span>
            </div>
            <div className="um-detail-row">
              <span className="um-detail-label">Email</span>
              <span className="um-detail-value">{user.email}</span>
            </div>
            <div className="um-detail-row">
              <span className="um-detail-label">Role</span>
              <span className="um-detail-value">
                <span className={`um-role-badge um-role-badge--${user.role}`}>
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </span>
              </span>
            </div>
            {user.assigned_modules?.length > 0 && (
              <div className="um-detail-row">
                <span className="um-detail-label">Modules</span>
                <span className="um-detail-value" style={{ display: 'flex', gap: 4 }}>
                  {user.assigned_modules.map((m) => (
                    <span key={m} className={`module-badge module-badge--${m}`}>
                      {m.charAt(0).toUpperCase() + m.slice(1)}
                    </span>
                  ))}
                </span>
              </div>
            )}
            <div className="um-detail-row">
              <span className="um-detail-label">Status</span>
              <span className="um-detail-value">
                <span className={`badge badge-${user.is_active ? 'closed' : 'lost'}`}>
                  <span className="badge-dot" />
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </span>
            </div>
            <div className="um-detail-row">
              <span className="um-detail-label">Created</span>
              <span className="um-detail-value">{formatDate(user.created_at)}</span>
            </div>
          </div>
        ) : (
          /* ── Edit mode ── */
          <form id="edit-user-form" onSubmit={handleSave}>
            <div className="form-group">
              <label className="section-label">Name *</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label className="section-label">Email *</label>
              <input
                className="input"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label className="section-label">Password (leave blank to keep current)</label>
              <div className="um-password-wrap">
                <input
                  className="input"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="••••••••"
                  minLength={form.password ? 6 : undefined}
                />
                <button
                  type="button"
                  className="um-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="section-label">Role *</label>
              <select
                className="input um-select"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              >
                <option value="admin">Admin</option>
                <option value="agent">Agent</option>
              </select>
            </div>

            {showModuleSelect && (
              <div className="form-group">
                <label className="section-label">Module Access *</label>
                <div className="um-checkbox-group">
                  {enabledModules.map((mod) => (
                    <label key={mod} className="um-checkbox-label">
                      <input
                        type="checkbox"
                        checked={form.assigned_modules.includes(mod)}
                        onChange={() => toggleModule(mod)}
                      />
                      <span>{mod.charAt(0).toUpperCase() + mod.slice(1)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </form>
        )}
      </div>

      <div className="modal-footer">
        {!editing ? (
          <>
            <div className="um-footer-left">
              {!isSelf && user.is_active && (
                <button type="button" className="btn btn-ghost btn-danger-text" onClick={() => onDeactivate(user)}>
                  Deactivate
                </button>
              )}
              {!user.is_active && (
                <button type="button" className="btn btn-ghost" onClick={handleActivate} disabled={activating}>
                  {activating ? 'Activating...' : 'Activate'}
                </button>
              )}
              {!isSelf && (
                <button type="button" className="btn btn-ghost btn-danger-text" onClick={() => onDelete(user)}>
                  Delete
                </button>
              )}
            </div>
            <div className="um-footer-right">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
              <button type="button" className="btn btn-primary" onClick={() => setEditing(true)}>Edit</button>
            </div>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
            <button type="submit" form="edit-user-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}


/* ─── Delete Confirmation Modal ─────────────────────────────────────────── */

function DeleteUserModal({ user, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setDeleting(true);
    setError('');
    try {
      await usersApi.delete(user.id);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user.');
      setDeleting(false);
    }
  };

  return (
    <Modal title="Delete User" size="sm" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}
        <p className="delete-msg">
          Are you sure you want to delete <strong>{user.name}</strong>?
        </p>
        <p className="delete-hint">
          All leads assigned to this user will be reassigned to you. This action cannot be undone.
        </p>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete User'}
        </button>
      </div>
    </Modal>
  );
}


/* ─── Deactivate Confirmation Modal ─────────────────────────────────────── */

function DeactivateUserModal({ user, onClose, onDone }) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setProcessing(true);
    setError('');
    try {
      await usersApi.update(user.id, { is_active: false });
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to deactivate.');
      setProcessing(false);
    }
  };

  return (
    <Modal title="Deactivate User" size="sm" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}
        <p className="delete-msg">
          Are you sure you want to deactivate <strong>{user.name}</strong>?
        </p>
        <p className="delete-hint">
          This user will no longer be able to log in. Their data and assigned leads will be preserved.
        </p>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={processing}>
          {processing ? 'Deactivating...' : 'Deactivate'}
        </button>
      </div>
    </Modal>
  );
}
